import type { ReactSVGElement } from 'react';
import { Children, cloneElement } from 'react';

import { getStyles } from '@shieldpay/theme-provider-ui/styles';
import type { themeVars } from '@shieldpay/theme-provider-ui/theme/contract';
import type { Color, Palette } from '@shieldpay/theme-provider-ui/theme/types';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import * as styles from './icon.css';

export type IconSize = keyof typeof themeVars.iconSizes;

export type IconProps = {
  className?: string;
  size?: IconSize;
  children?: ReactSVGElement;
  'data-testid'?: string;
} & ( // type and color cannot be used together
  | {
      type: IconType;
      color?: never;
    }
  | {
      type?: never;
      color?: Color;
    }
);

const iconColors = {
  default: 'neutral500',
  info: 'info500',
  success: 'success500',
  warning: 'warning500',
  error: 'alert500',
} satisfies Record<string, Palette>;

export type IconType = keyof typeof iconColors;

export const Icon = ({
  className,
  size = 'm',
  children,
  type,
  color = 'currentColor',
  'data-testid': testId,
}: IconProps) => {
  // Pass down props to rendered SVG
  const svgChildren = Children.map(children, (child) =>
    child
      ? cloneElement(child, {
          className: styles.svg,
          preserveAspectRatio: 'none',
        })
      : undefined,
  );

  const iconColor = (type && iconColors[type]) || color;

  return (
    <span
      aria-hidden="true"
      className={classFilter([
        styles.icon,
        styles.iconSize[size],
        getStyles({ color: iconColor }),
        className,
      ])}
      data-testid={testId}
    >
      {svgChildren}
    </span>
  );
};
